import React from "react";
import { CDNImage } from "./cdn-image";
import { cn } from "~/lib/cn";
import { safeBuilderHtml } from "~/lib/builder-utils";

export interface AuthorGridItem {
  id: string;
  name: string;
  image: string;
  title?: string;
  subtitle?: string;
  mainText?: unknown; // Rich text content
}

export interface AuthorGridProps {
  authors: AuthorGridItem[];
  columns?: "1" | "2" | "3" | "4";
  gap?: "sm" | "md" | "lg";
  imageSize?: "sm" | "md" | "lg";
  className?: string;
}

const getColumnsClass = (columns: string = "3") => {
  switch (columns) {
    case "1":
      return "grid-cols-1";
    case "2":
      return "grid-cols-1 md:grid-cols-2";
    case "3":
      return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
    case "4":
      return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";
    default:
      return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
  }
};

const getGapClass = (gap: string = "md") => {
  switch (gap) {
    case "sm":
      return "gap-4";
    case "md":
      return "gap-6";
    case "lg":
      return "gap-8";
    default:
      return "gap-6";
  }
};

const getImageSizeProps = (size: string = "md") => {
  switch (size) {
    case "sm":
      return { width: 240, height: 320, className: "w-32 h-40" };
    case "md":
      return { width: 288, height: 384, className: "w-36 h-48" };
    case "lg":
      return { width: 336, height: 448, className: "w-44 h-56" };
    default:
      return { width: 288, height: 384, className: "w-36 h-48" };
  }
};

export const AuthorGrid: React.FC<AuthorGridProps> = ({
  authors,
  columns = "3",
  gap = "md",
  imageSize = "md",
  className,
}) => {
  const imageProps = getImageSizeProps(imageSize);

  return (
    <div className={cn("w-full", className)}>
      <div className={cn("grid", getColumnsClass(columns), getGapClass(gap))}>
        {authors.map((author) => (
          <div key={author.id} className="flex gap-4 group">
            <div
              className={cn(
                "relative flex-shrink-0 overflow-hidden rounded-2xl transition-transform group-hover:scale-105",
                imageProps.className,
              )}
            >
              <CDNImage
                src={author.image}
                alt={author.name}
                width={imageProps.width}
                height={imageProps.height}
                className="w-full h-full object-cover object-center transition-transform duration-300"
              />
              {(author.title || author.subtitle) && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
              )}
              {(author.title || author.subtitle) && (
                <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                  {author.title && (
                    <h3 className="text-xs font-semibold leading-tight">
                      {author.title}
                    </h3>
                  )}
                  {author.subtitle && (
                    <p className="text-xs opacity-90 mt-1">{author.subtitle}</p>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-col justify-start space-y-3">
              <h4 className="font-heading text-lg font-semibold text-gray-900 leading-tight">
                {author.name}
              </h4>
              {author.mainText ? (
                <div
                  className="prose prose-sm text-gray-600 max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: safeBuilderHtml(author.mainText),
                  }}
                />
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
